import { ValueFilterEntry } from 'gql/autogen/ValueFilterEntry';
import { RangeFilterEntry } from 'gql/autogen/RangeFilterEntry';
import { RangeFilterMatrix } from 'gql/autogen/RangeFilterMatrix';
import { ValueFilterMatrix } from 'gql/autogen/ValueFilterMatrix';
import { RangeEntry } from 'gql/autogen/RangeEntry';
import { RangeBound } from 'gql/autogen/RangeBound';
import { FilterCode } from 'gql/autogen/FilterCode';
import { Optional } from 'core/type/generic';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { BookingFeatureCms, BookingFeatureId, DurationCms, DurationId, PriceFilterType, RangeFilter, ValueFilter } from 'core/type/Filter.types';
import { mapCategoryFilter, mapPriceFilter, mapRatingFilter, mapSubcategoryFilter, mapValueFilter } from 'gql/service/graph/activityService';
import { CategoryCms, CategoryIcon, CategoryId, CategorySubLabelCms, CategoryTabTitleCms } from 'core/type/Category.types';
import { CategoryValueFilter, CategoryWithSubcategoryValueFilter, SubcategoryValueFilter } from 'component/page/search/SortAndFilter/CategoryFilter/CategoryFilter.types';
import { CategoryApiIdToQueryKey } from 'component/page/search/SortAndFilter/SortAndFilterQuery.types';
import { FilterState } from 'component/common/Filter/Filter.types';
import { SubcategoryCms, SubcategoryId, SubcategoryParentId } from 'core/type/Subcategory.types';

export const getValueFilterEntry = (code: string, valueFilterEntries?: ValueFilterEntry[]): Optional<ValueFilterEntry> =>
    valueFilterEntries?.find((v) => v.code.toString() === code);
export const getRangeFilterEntry = (code: string, rangeFilterEntries?: RangeFilterEntry[]): Optional<RangeFilterEntry> =>
    rangeFilterEntries?.find((v) => v.code.toString() === code);
export const getValueFilterCode = (value?: ValueFilterMatrix) => value?.code || '';
export const getRangeFilterCode = (range?: RangeFilterMatrix) => range?.code || '';
export const getRangeFilterRanges = (range?: RangeFilterEntry) => range?.ranges || [];
export const getRangeEntryLowerBoundValue = (range?: RangeEntry) => range?.lowerBound?.value || 0;
export const getRangeEntryUpperBoundValue = (range?: RangeEntry) => range?.upperBound?.value || 0;
export const getRangeFilterMin = (range?: RangeFilterMatrix) => range?.min || 0;
export const getRangeFilterMax = (range?: RangeFilterMatrix) => range?.max || 0;

export const getFilterStateByValue = (code: FilterCode, values: string[], selected: boolean, state: FilterState): FilterState => {
    const newState: FilterState = {
        rangeFilterMatrix: state.rangeFilterMatrix,
        valueFilterMatrix: state.valueFilterMatrix.filter((f) => f.code !== code),
    };

    let filterValues = state.valueFilterMatrix.find((f) => f.code === code)?.values || [];

    if (selected) {
        values.forEach((v) => (filterValues = [...filterValues, v]));
        newState.valueFilterMatrix.push({ code, values: filterValues });
    } else {
        const filteredValues = filterValues.filter((f) => !values.includes(f)) || [];

        if (filteredValues.length) {
            newState.valueFilterMatrix = [...newState.valueFilterMatrix, { code, values: filteredValues }];
        }
    }

    return newState;
};

// remove this method and use getFilterStateByValue instead when multiple categories are supported
export const getFilterStateForOneValue = (code: FilterCode, values: string[], state: FilterState): FilterState => {
    const newState: FilterState = {
        rangeFilterMatrix: state.rangeFilterMatrix,
        valueFilterMatrix: state.valueFilterMatrix.filter((f) => f.code !== code),
    };

    if (values.length > 0) {
        newState.valueFilterMatrix.push({ code, values: [values[0]] });
    } else {
        newState.valueFilterMatrix = newState.valueFilterMatrix.filter((f) => f.code !== code);
    }

    return newState;
};

export const getFilterStateByRange = (
    code: FilterCode,
    lowerBound: RangeBound,
    upperBound: RangeBound,
    selected: boolean,
    state: FilterState,
    rangeFilterMatrix?: RangeFilterMatrix[],
): FilterState => {
    const newState: FilterState = {
        rangeFilterMatrix: state.rangeFilterMatrix.filter((f) => f.code !== code),
        valueFilterMatrix: state.valueFilterMatrix,
    };

    const result: RangeFilterEntry = { code, ranges: [] };

    if (code === FilterCode.Price && `${lowerBound.value}${upperBound.value}` !== '00') {
        const priceFilter = mapPriceFilter(rangeFilterMatrix);
        const min = getRangeFilterMin(priceFilter);
        const max = getRangeFilterMax(priceFilter);

        if (!(lowerBound.value === min && upperBound.value === max)) {
            result.ranges.push({ lowerBound, upperBound });
        }
    }

    if (code !== FilterCode.Price) {
        const rangeFilter = state.rangeFilterMatrix.find((f) => f.code === code);
        const rangeValues = rangeFilter?.ranges || [];
        if (selected) {
            result.ranges = [...rangeValues, { lowerBound, upperBound }];
        } else {
            result.ranges = rangeValues.filter((f) => f.lowerBound?.value !== lowerBound?.value && f.upperBound?.value !== upperBound?.value);
        }
    }

    if (result.ranges.length) {
        newState.rangeFilterMatrix = [...newState.rangeFilterMatrix, result];
    }

    return newState;
};

const isSelected = (code: string, value: string, valueFilterEntry?: ValueFilterEntry): boolean => {
    return valueFilterEntry?.values?.some((v) => v === value) || false;
};

export const calculateMinAndMaxPrice = (range: RangeEntry, min: number, max: number) => {
    const lowerBound = getRangeEntryLowerBoundValue(range);
    const upperBound = getRangeEntryUpperBoundValue(range);

    const selectedMax = range && upperBound >= min ? Math.min(upperBound, max) : max;
    let selectedMin = range && lowerBound <= max ? Math.max(lowerBound, min) : min;
    selectedMin = selectedMin > selectedMax ? min : selectedMin;

    return { selectedMin, selectedMax };
};

export const getPriceFilter = (cms: CmsValue, rangeFilterEntries: RangeFilterEntry[], rangeFilterMatrix?: RangeFilterMatrix[]): Optional<PriceFilterType> => {
    const priceFilter = mapPriceFilter(rangeFilterMatrix);
    if (!Object.keys(priceFilter).length) return undefined;

    const code = getRangeFilterCode(priceFilter) as FilterCode;
    const min = 0;
    const max = getRangeFilterMax(priceFilter);

    const priceFilterEntry = getRangeFilterEntry(code, rangeFilterEntries);
    const ranges = getRangeFilterRanges(priceFilterEntry);
    const { selectedMin, selectedMax } = calculateMinAndMaxPrice(ranges[0], min, max);

    return { code, min, max, selectedMin, selectedMax, label: cms[146542] };
};

export const getStarRatingItems = (cms: CmsValue, rangeFilterEntries: RangeFilterEntry[], rangeFilterMatrix?: RangeFilterMatrix[]): RangeFilter[] => {
    const ratingFilter = mapRatingFilter(rangeFilterMatrix);
    if (!Object.keys(ratingFilter).length) {
        return [];
    }

    const code = getRangeFilterCode(ratingFilter);
    const priceFilterEntry = getRangeFilterEntry(code, rangeFilterEntries);
    const result: RangeFilter[] = [...Array<number>(getRangeFilterMax(ratingFilter) + 1)]
        .map((v, i) => i)
        .reverse()
        .map((index) => {
            const min = index;
            const max = index + 1;
            return {
                min,
                max,
                code,
                label: (`${min}${max}` === '01' && cms[150030]) || '',
                selected: priceFilterEntry?.ranges?.some((r: RangeEntry) => r.lowerBound?.value === min && r.upperBound?.value === max) || false,
            } as RangeFilter;
        });

    return result;
};

export const getDurationItems = (cms: CmsValue, valueFilterEntries: ValueFilterEntry[], valueFilterMatrix?: ValueFilterMatrix[]): ValueFilter[] => {
    const durationFilter = mapValueFilter(FilterCode.Duration, valueFilterMatrix);
    if (!durationFilter?.availableValue?.length) return [];

    const durationCode = getValueFilterCode(durationFilter) as FilterCode;
    const result: ValueFilter[] = [];
    const durationFilterEntry = getValueFilterEntry(durationCode, valueFilterEntries);
    durationFilter.availableValue?.forEach((item) => {
        if (!item.id) return;

        const index = result.findIndex((r) => r.values.includes(DurationId.Between4And244Hours));

        if (index > -1) {
            result[index].values = [...result[index].values, item.id];
        } else {
            result.push({
                selected: isSelected(durationCode, item.id, durationFilterEntry),
                code: durationCode,
                values: [item.id],
                label: cms[DurationCms[item.id as DurationId]],
            });
        }
    });

    return result;
};

export const getBookingFeatureItems = (cms: CmsValue, valueFilterEntries: ValueFilterEntry[], valueFilterMatrix?: ValueFilterMatrix[]): ValueFilter[] => {
    const bookingFeatureFilter = mapValueFilter(FilterCode.BookingFeature, valueFilterMatrix);
    if (!bookingFeatureFilter?.availableValue?.length) return [];

    const bookingFeatureCode = getValueFilterCode(bookingFeatureFilter) as FilterCode;
    const result: ValueFilter[] = [];
    const bookingFeatureFilterEntry = getValueFilterEntry(bookingFeatureCode, valueFilterEntries);
    bookingFeatureFilter.availableValue?.forEach((item) => {
        if (!item.id) return;

        result.push({
            selected: isSelected(bookingFeatureCode, item.id, bookingFeatureFilterEntry),
            code: bookingFeatureCode,
            values: [item.id],
            label: cms[BookingFeatureCms[item.id as BookingFeatureId]],
        });
    });

    return result;
};

export const getCategoryItems = (cms: CmsValue, valueFilterEntries: ValueFilterEntry[], valueFilterMatrix?: ValueFilterMatrix[]): CategoryValueFilter[] => {
    const categoryFilter = mapCategoryFilter(valueFilterMatrix);
    if (!categoryFilter?.availableValue?.length) return [];

    const categoryCode = getValueFilterCode(categoryFilter);
    const categoryFilterEntry = getValueFilterEntry(categoryCode, valueFilterEntries);

    const allCategoryMatrixItem = categoryFilter.availableValue?.find((item) => item.id === CategoryId.All);

    const result: CategoryValueFilter[] = [];

    if (allCategoryMatrixItem) {
        result.push({
            selected: !categoryFilterEntry?.values?.length,
            values: [],
            label: cms[CategoryCms[CategoryId.All]],
            subLabel: '',
            tabLabel: cms[CategoryTabTitleCms[CategoryId.All]],
            asset: CategoryIcon[CategoryId.All],
            count: allCategoryMatrixItem.count || 0,
        });
    }

    categoryFilter.availableValue?.forEach((item) => {
        if (!item.id || !CategoryApiIdToQueryKey[item.id]) return; // All category will have no CategoryApiIdToQueryKey, so it will skip re-adding it here

        const subLabelCmsId = CategorySubLabelCms[item.id as CategoryId];
        const subLabel = subLabelCmsId ? cms[subLabelCmsId] : '';
        result.push({
            selected: isSelected(categoryCode, item.id, categoryFilterEntry),
            values: [item.id as CategoryId],
            label: cms[CategoryCms[item.id as CategoryId]],
            tabLabel: cms[CategoryTabTitleCms[item.id as CategoryId]],
            asset: CategoryIcon[item.id as CategoryId],
            count: item.count || 0,
            subLabel,
        });
    });

    return result;
};

export const getCategoryWithSubcategoryItems = (
    cms: CmsValue,
    valueFilterEntries: ValueFilterEntry[],
    valueFilterMatrix?: ValueFilterMatrix[],
): CategoryWithSubcategoryValueFilter[] => {
    const categoryFilter = mapCategoryFilter(valueFilterMatrix);
    if (!categoryFilter?.availableValue?.length) return [];

    const subcategoryFilter = mapSubcategoryFilter(valueFilterMatrix);

    const categoryCode = getValueFilterCode(categoryFilter);
    const subcategoryCode = getValueFilterCode(subcategoryFilter);
    const categoryFilterEntry = getValueFilterEntry(categoryCode, valueFilterEntries);

    const categoryFilterWithoutAll = categoryFilter.availableValue?.filter((item) => item.id !== CategoryId.All);

    const result: CategoryWithSubcategoryValueFilter[] = [];

    categoryFilterWithoutAll?.forEach((item) => {
        if (!item.id || !CategoryApiIdToQueryKey[item.id]) return; // All category will have no CategoryApiIdToQueryKey, so it will skip re-adding it here

        const isCategorySelected = isSelected(categoryCode, item.id, categoryFilterEntry);
        const subLabelCmsId = CategorySubLabelCms[item.id as CategoryId];
        const subLabel = subLabelCmsId ? cms[subLabelCmsId] : '';
        const subcategoryMatrix = subcategoryFilter.availableValue?.filter((subcategory) => SubcategoryParentId[subcategory.id as SubcategoryId] === item.id) || [];
        const subcategories = subcategoryMatrix.map((subcategory): SubcategoryValueFilter => {
            const isSubcategorySelected = isSelected(subcategoryCode, subcategory.id || '', getValueFilterEntry(subcategoryCode, valueFilterEntries));
            return {
                id: subcategory.id as SubcategoryId,
                label: cms[SubcategoryCms[subcategory.id as SubcategoryId]],
                count: subcategory.count || 0,
                selected: isSubcategorySelected,
                showAsSelected: isCategorySelected || isSubcategorySelected,
            };
        });
        const isIndeterminate = !isCategorySelected && subcategories.some((subcategory) => subcategory.selected) && subcategories.some((subcategory) => !subcategory.selected);
        const filteredSubcategories = subcategories.filter((subcategory) => subcategory.count > 0 || subcategory.selected);
        result.push({
            selected: isCategorySelected,
            value: item.id as CategoryId,
            label: cms[CategoryCms[item.id as CategoryId]],
            tabLabel: cms[CategoryTabTitleCms[item.id as CategoryId]],
            asset: CategoryIcon[item.id as CategoryId],
            count: item.count || 0,
            subLabel,
            subcategories: filteredSubcategories,
            isIndeterminate,
        });
    });

    return result;
};

export const getSubcategoryItems = (cms: CmsValue, valueFilterEntries: ValueFilterEntry[], valueFilterMatrix?: ValueFilterMatrix[]): SubcategoryValueFilter[] => {
    const subcategoryFilter = mapSubcategoryFilter(valueFilterMatrix);
    if (!subcategoryFilter?.availableValue?.length) return [];

    const subcategoryCode = getValueFilterCode(subcategoryFilter);
    const categoryCode = getValueFilterCode(mapCategoryFilter(valueFilterMatrix));
    const subcategoryFilterEntry = getValueFilterEntry(subcategoryCode, valueFilterEntries);
    const categoryFilterEntry = getValueFilterEntry(categoryCode, valueFilterEntries);

    const result: SubcategoryValueFilter[] = [];

    subcategoryFilter.availableValue?.forEach((item) => {
        if (!item.id) return;

        const selected = isSelected(subcategoryCode, item.id, subcategoryFilterEntry);
        const parentId = SubcategoryParentId[item.id as SubcategoryId];
        const parentSelected = parentId ? isSelected(categoryCode, parentId, categoryFilterEntry) : false;
        result.push({
            id: item.id as SubcategoryId,
            label: cms[SubcategoryCms[item.id as SubcategoryId]],
            count: item.count || 0,
            selected,
            showAsSelected: selected || parentSelected,
        });
    });

    return result;
};
